import React, { useState } from 'react';
import { Settings, DollarSign, Package, Truck, Save, AlertCircle, TrendingUp, LogOut, User, Edit, RotateCcw } from 'lucide-react';
import { mockDeliveryCharges, calculateMonthlyRental } from '../mock';
import { useAuth } from '../hooks/useAuth';
import { useData } from '../hooks/useData';
import { backendService } from '../lib/api';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const [deliveryCharges, setDeliveryCharges] = useState(mockDeliveryCharges);
  const [costRecoveryMonths, setCostRecoveryMonths] = useState(3); // Default to 3 months
  const [activeTab, setActiveTab] = useState('pricing');
  const [saveStatus, setSaveStatus] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [localPricingConfig, setLocalPricingConfig] = useState({});
  const [isPricingEditing, setIsPricingEditing] = useState(false);
  const { admin, logout } = useAuth();
  const { pricingConfig, linenTypes, isLoading, error, refreshData } = useData();
  const navigate = useNavigate();

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate('/admin-login');
    }
  };

  const fetchQuotes = async () => {
    setQuotesLoading(true);
    try {
      const response = await backendService.request('/quotes', { method: 'GET' });
      console.log('Fetched quotes:', response.quotes);
      setQuotes(response.quotes);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setQuotesLoading(false);
    }
  };

  // Sync local pricing config with data from hook
  React.useEffect(() => {
    if (pricingConfig && Object.keys(pricingConfig).length > 0) {
      setLocalPricingConfig(pricingConfig);
    }
  }, [pricingConfig]);

  // Fetch quotes when quotes tab is active
  React.useEffect(() => {
    if (activeTab === 'quotes') {
      fetchQuotes();
    }
  }, [activeTab]);

  const updateItemPricing = (itemId, field, value) => {
    const numValue = parseFloat(value) || 0;
    setLocalPricingConfig(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: numValue,
        // Only auto-calculate rental price if cost changes and rental price hasn't been manually set
        ...(field === 'cost' && !prev[itemId]?.rentalPriceManuallySet ? { 
          rentalPrice: numValue / costRecoveryMonths 
        } : {}),
        // Mark rental price as manually set if user edits it directly
        ...(field === 'rentalPrice' ? { rentalPriceManuallySet: true } : {})
      }
    }));
  };

  const updateCostRecoveryPeriod = (newMonths) => {
    setCostRecoveryMonths(newMonths);
    // Recalculate rental prices only for items that haven't been manually set
    setLocalPricingConfig(prev => {
      const updated = {};
      Object.entries(prev).forEach(([itemId, config]) => {
        updated[itemId] = {
          ...config,
          // Only auto-calculate if rental price hasn't been manually set
          ...(config.rentalPriceManuallySet ? {} : { rentalPrice: config.cost / newMonths })
        };
      });
      return updated;
    });
  };

  const updateDeliveryCharges = (type, value) => {
    setDeliveryCharges(prev => ({
      ...prev,
      [type]: parseFloat(value) || 0
    }));
  };

  const handlePricingEdit = () => {
    setIsPricingEditing(true);
  };

  const handlePricingCancel = () => {
    setIsPricingEditing(false);
    // Reset local pricing config to original data
    if (pricingConfig && Object.keys(pricingConfig).length > 0) {
      setLocalPricingConfig(pricingConfig);
    }
    // Clear any existing save status
    setSaveStatus('');
  };

  const handlePricingSave = async () => {
    setSaveStatus('saving');
    try {
      // Get CSRF token first
      await backendService.getCsrfToken();

      // Prepare the pricing config data for the backend
      const pricingData = {};
      Object.entries(localPricingConfig).forEach(([itemId, config]) => {
        pricingData[itemId] = {
          cost: config.cost,
          rentalPrice: config.rentalPrice,
          launderingPrice: config.launderingPrice,
        };
      });

      // Send the update request to the backend
      console.log('Sending pricing data:', pricingData);
      const response = await backendService.request('/items/pricing', {
        method: 'PUT',
        body: JSON.stringify({ pricingConfig: pricingData })
      });

      console.log('Backend response:', response);

      if (response.success) {
        // Refresh the data to get the updated values
        await refreshData();
        setIsPricingEditing(false);
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } else {
        throw new Error(response.message || 'Failed to save pricing configuration');
      }
    } catch (error) {
      console.error('Error saving pricing configuration:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const saveSettings = async () => {
    setSaveStatus('saving');
    try {
      // TODO: Implement actual save to database
      // For now, just refresh the data
      await refreshData();
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const handleViewDetails = (quote) => {
    navigate(`/admin/quote/${quote.id}`);
  };

  const tabs = [
    { id: 'pricing', label: 'Item Pricing', icon: DollarSign },
    { id: 'delivery', label: 'Delivery Charges', icon: Truck },
    { id: 'quotes', label: 'Quote Requests', icon: Package },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp }
  ];

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={refreshData}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="display-medium text-gray-900 mb-2">Admin Dashboard</h1>
              <p className="body-large text-gray-600">Manage pricing and configuration</p>
            </div>
            
            <div className="flex items-center gap-4">
              {admin && (
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-4 h-4" />
                  <span className="body-medium">{admin.name}</span>
                </div>
              )}
              
              
              <button 
                onClick={handleLogout}
                className="btn-secondary flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>

          {/* Save Status */}
          {saveStatus === 'saved' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-800">Settings saved successfully!</span>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-8">
            <div className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 border-b-2 font-medium ${
                    activeTab === tab.id
                      ? 'border-red-500 text-red-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Pricing Tab */}
          {activeTab === 'pricing' && (
            <div className="space-y-8">
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-gray-600" />
                    <h2 className="heading-2 text-gray-900">Item Pricing Configuration</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isPricingEditing ? (
                      <button
                        onClick={handlePricingEdit}
                        className="btn-secondary flex items-center gap-2"
                      >
                        <Edit className="w-4 h-4" />
                        Edit Pricing
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={handlePricingSave}
                          className="btn-primary flex items-center gap-2"
                          disabled={saveStatus === 'saving'}
                        >
                          <Save className="w-4 h-4" />
                          {saveStatus === 'saving' ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          onClick={handlePricingCancel}
                          className="btn-secondary flex items-center gap-2"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h3 className="font-medium text-blue-900 mb-4">Pricing Formula</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                    <div>
                      <p className="text-blue-800 text-sm mb-2">
                        Monthly rental price = Cost ÷ Recovery Period
                      </p>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-blue-900">
                          Cost Recovery Period (months):
                        </label>
                        {isPricingEditing ? (
                          <input
                            type="number"
                            value={costRecoveryMonths}
                            onChange={(e) => updateCostRecoveryPeriod(Math.max(1, parseFloat(e.target.value) || 1))}
                            className="w-20 px-2 py-1 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            min="1"
                            step="0.5"
                          />
                        ) : (
                          <span className="font-medium text-blue-900">{costRecoveryMonths}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-800 text-sm">
                        Current setting: Recover cost in <strong>{costRecoveryMonths}</strong> months
                      </p>
                      <p className="text-blue-600 text-xs mt-1">
                        Lower = Higher monthly rental | Higher = Lower monthly rental
                      </p>
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Item</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Your Cost ($)</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Monthly Rental ($)</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Laundering Cost ($)</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linenTypes.map((item) => {
                        const pricing = localPricingConfig[item.id];
                        if (!pricing) return null; // Skip if pricing not loaded yet
                        return (
                          <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-4">
                              <div>
                                <div className="font-medium text-gray-900">{item.name}</div>
                                <div className="text-sm text-gray-500 capitalize">{item.category}</div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              {isPricingEditing ? (
                                <input
                                  type="number"
                                  value={pricing.cost}
                                  onChange={(e) => updateItemPricing(item.id, 'cost', e.target.value)}
                                  onBlur={(e) => updateItemPricing(item.id, 'cost', parseFloat(e.target.value).toFixed(2))}
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                  step="0.01"
                                  min="0"
                                />
                              ) : (
                                <span className="font-medium text-gray-900">${parseFloat(pricing.cost).toFixed(2)}</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {isPricingEditing ? (
                                <input
                                  type="number"
                                  value={pricing.rentalPrice}
                                  onChange={(e) => updateItemPricing(item.id, 'rentalPrice', e.target.value)}
                                  onBlur={(e) => updateItemPricing(item.id, 'rentalPrice', parseFloat(e.target.value).toFixed(2))}
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                  step="0.01"
                                  min="0"
                                />
                              ) : (
                                <div>
                                  <div className="font-medium text-green-600">
                                    ${pricing.rentalPrice.toFixed(2)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {pricing.rentalPriceManuallySet ? 'Manual' : 'Auto-calculated'}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {isPricingEditing ? (
                                <input
                                  type="number"
                                  value={pricing.launderingPrice}
                                  onChange={(e) => updateItemPricing(item.id, 'launderingPrice', e.target.value)}
                                  onBlur={(e) => updateItemPricing(item.id, 'launderingPrice', parseFloat(e.target.value).toFixed(2))}
                                  className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                  step="0.01"
                                  min="0"
                                />
                              ) : (
                                <span className="font-medium text-gray-900">${parseFloat(pricing.launderingPrice).toFixed(2)}</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {isPricingEditing && (
                                <button 
                                  onClick={() => {
                                    setLocalPricingConfig(prev => ({
                                      ...prev,
                                      [item.id]: {
                                        ...prev[item.id],
                                        rentalPrice: prev[item.id].cost / costRecoveryMonths,
                                        rentalPriceManuallySet: false
                                      }
                                    }));
                                  }}
                                  className="text-red-500 hover:text-red-700 text-sm"
                                  title="Reset to auto-calculated price"
                                >
                                  Reset
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                
                {/* Save Status Message */}
                {saveStatus && (
                  <div className={`mt-4 p-3 rounded-lg ${
                    saveStatus === 'saved' 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : saveStatus === 'error'
                      ? 'bg-red-50 border border-red-200 text-red-800'
                      : 'bg-blue-50 border border-blue-200 text-blue-800'
                  }`}>
                    {saveStatus === 'saving' && 'Saving changes...'}
                    {saveStatus === 'saved' && 'Pricing configuration saved successfully!'}
                    {saveStatus === 'error' && 'Error saving pricing configuration. Please try again.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Delivery Tab */}
          {activeTab === 'delivery' && (
            <div className="space-y-8">
              <div className="bg-white rounded-lg p-6">
                <div className="flex items-center gap-2 mb-6">
                  <Truck className="w-5 h-5 text-gray-600" />
                  <h2 className="heading-2 text-gray-900">Delivery & Pickup Charges</h2>
                </div>

                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="font-medium text-yellow-900 mb-2">Charge Structure</h3>
                  <p className="text-yellow-800 text-sm">
                    These charges are applied per visit, regardless of the number of items being 
                    picked up or delivered. Customers pay both pickup and delivery charges for each service cycle.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div>
                      <label className="block font-medium text-gray-900 mb-2">
                        Pickup Charge (per visit)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3 text-gray-500">$</span>
                        <input
                          type="number"
                          value={deliveryCharges.pickup}
                          onChange={(e) => updateDeliveryCharges('pickup', e.target.value)}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Charged when we collect linens from customer's property
                      </p>
                    </div>

                    <div>
                      <label className="block font-medium text-gray-900 mb-2">
                        Delivery Charge (per visit)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-3 text-gray-500">$</span>
                        <input
                          type="number"
                          value={deliveryCharges.delivery}
                          onChange={(e) => updateDeliveryCharges('delivery', e.target.value)}
                          className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          step="0.01"
                          min="0"
                        />
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Charged when we deliver clean linens back to property
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="font-medium text-gray-900 mb-4">Sample Visit Cost</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Pickup charge:</span>
                        <span className="font-medium">${deliveryCharges.pickup.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Delivery charge:</span>
                        <span className="font-medium">${deliveryCharges.delivery.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-gray-200 pt-3">
                        <div className="flex justify-between">
                          <span className="font-medium text-gray-900">Total per service cycle:</span>
                          <span className="font-bold text-lg text-red-600">
                            ${(deliveryCharges.pickup + deliveryCharges.delivery).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="heading-2 text-gray-900">Quote Requests</h2>
                <button 
                  onClick={fetchQuotes}
                  disabled={quotesLoading}
                  className="btn-secondary flex items-center gap-2"
                >
                  {quotesLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <Package className="w-4 h-4" />
                      Refresh
                    </>
                  )}
                </button>
              </div>

              {quotesLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading quotes...</p>
                </div>
              ) : quotes.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Quote Requests</h3>
                  <p className="text-gray-600">No quote requests have been submitted yet.</p>
                </div>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Customer
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Monthly Rental
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Properties
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Per Visit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Submitted
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {quotes.map((quote) => {
                          console.log('Quote object:', quote);
                          return (
                          <tr key={quote.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {quote.customer_name}
                                </div>
                                <div className="text-sm text-gray-500">
                                  #{quote.id}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm text-gray-900">
                                  {quote.customer_email}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {quote.customer_phone}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                quote.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                quote.status === 'reviewed' ? 'bg-blue-100 text-blue-800' :
                                quote.status === 'approved' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {quote.status_label || quote.status || 'Unknown'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${quote.monthly_rental}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {quote.total_properties_count}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${quote.total_per_visit}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(quote.created_at).toLocaleDateString()}
                              <br />
                              <span className="text-xs">
                                {new Date(quote.created_at).toLocaleTimeString()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex gap-2">
                                <button 
                                  onClick={() => handleViewDetails(quote)}
                                  className="text-indigo-600 hover:text-indigo-900 bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded-md text-xs font-medium transition-colors"
                                >
                                  View Details
                                </button>
                                {quote.admin_notes && (
                                  <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded" title={quote.admin_notes}>
                                    Has Notes
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-gray-600">
                        Total Quotes: <span className="font-medium text-gray-900">{quotes.length}</span>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-gray-600">
                          Pending: <span className="font-medium text-yellow-600">{quotes.filter(q => q.status === 'pending').length}</span>
                        </span>
                        <span className="text-gray-600">
                          Reviewed: <span className="font-medium text-blue-600">{quotes.filter(q => q.status === 'reviewed').length}</span>
                        </span>
                        <span className="text-gray-600">
                          Approved: <span className="font-medium text-green-600">{quotes.filter(q => q.status === 'approved').length}</span>
                        </span>
                        <span className="text-gray-600">
                          Rejected: <span className="font-medium text-red-600">{quotes.filter(q => q.status === 'rejected').length}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Total Items</h3>
                      <p className="text-sm text-gray-600">Across all properties</p>
                    </div>
                  </div>
                  <p className="heading-1 text-gray-900">1,247</p>
                  <p className="text-sm text-green-600 mt-1">↑ 12% from last month</p>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Monthly Revenue</h3>
                      <p className="text-sm text-gray-600">Rental fees</p>
                    </div>
                  </div>
                  <p className="heading-1 text-gray-900">$12,450</p>
                  <p className="text-sm text-green-600 mt-1">↑ 8% from last month</p>
                </div>

                <div className="bg-white rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Truck className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Active Properties</h3>
                      <p className="text-sm text-gray-600">With ongoing rentals</p>
                    </div>
                  </div>
                  <p className="heading-1 text-gray-900">84</p>
                  <p className="text-sm text-green-600 mt-1">↑ 5% from last month</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6">
                <h2 className="heading-2 text-gray-900 mb-6">Popular Items</h2>
                <div className="space-y-4">
                  {linenTypes.slice(0, 5).map((item, index) => {
                    const pricing = localPricingConfig[item.id];
                    if (!pricing) return null; // Skip if pricing not loaded yet
                    return (
                      <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium">
                            {index + 1}
                          </span>
                          <div>
                            <h3 className="font-medium text-gray-900">{item.name}</h3>
                            <p className="text-sm text-gray-600">${pricing.rentalPrice}/month</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-gray-900">{Math.floor(Math.random() * 200) + 50} units</p>
                          <p className="text-sm text-gray-600">Currently rented</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;